'use client'

import { X } from 'lucide-react'
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'

type Props = {
  /** Accessible label for the dialog (used as aria-label). */
  title: string
  onClose: () => void
  children: React.ReactNode
}

/**
 * Lightweight media modal — a dark-backdrop dialog used to play a single video
 * or show a Matterport tour. Closes on backdrop click, the × button, or Escape;
 * locks body scroll while open. Only ever rendered on the client (mounted on a
 * user interaction), so the portal target is always available.
 */
export function MediaModal({ title, onClose, children }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="popup-fade fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center text-white transition-opacity hover:opacity-70"
        onClick={onClose}
      >
        <X className="h-6 w-6" />
      </button>

      <div
        className="popup-pop relative w-full max-w-[1280px]"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
